import { type ZodError } from "zod";

type NoanApplicationErrorContext = Record<string, unknown> | string;

export type ErrorCode = 404 | 403 | 500;

export enum NoanError {
  InternalApiError = "Unknown internal API error",
  ExternalApiError = "Unknown third-party API error",
  Invariant = "Invariant error",
  InvalidPromptMissingUserItem = "Invalid generator prompt - missing user item",
  InvalidConnector = "Invalid connector",
  InvalidArguments = "Invalid arguments",
  MissingIdentifier = "Missing a valid resource identifier",
  MissingStripeCustomerId = "Account does not have a valid stripe customer ID",
  MissingPermission = "Account does not have permission to complete this action",
  MissingServiceIdentity = "Unable to resolve service identity for job",
  LLMExpectationsFailure = "LLM response does not meet expectations",
  IdentityNotFound = "Identity not found",
  ResourceNotFound = "Resource not found",
  ResourceNotAllowed = "Access to resource not allowed",
  ResourceAlreadyExists = "Resource already exists",
  MultipleResourcesFound = "Multiple resources found",
  NotLoggedIn = "User not logged in",
  Unauthenticated = "Missing or invalid credentials",
  FileImportError = "File import error",
  RateLimitExceeded = "Rate limit exceeded",
  FactCheckError = "Fact-checking pipeline failed",
  ContextGraphFetchError = "Context Graph API fetching error",
  SubscriptionInsufficientSeats = "Subscription does not have enough seats",
  SubscriptionPaymentFailed = "Subscription payment failed",
  AccountInactive = "Account is inactive",
  PlanLimitReached = "Plan limit reached",
}

export type ErrorResponse = {
  code: keyof typeof NoanError;
  message: string;
};

export class NoanApplicationError extends Error {
  constructor(
    public readonly errorType: NoanError,
    public readonly ctx: NoanApplicationErrorContext,
  ) {
    super(`${errorType}${typeof ctx === "string" ? `: ${ctx}` : ""}`);
    this.name = `${errorType}`;
  }

  toString() {
    return `${this.errorType}: ${JSON.stringify(this.ctx)}`;
  }
}

export function generateNoanError(
  errorType: NoanError,
  additionalContext: NoanApplicationErrorContext = {},
) {
  return new NoanApplicationError(errorType, additionalContext);
}

export function isNoanError(error: Error): error is NoanApplicationError {
  return (error as NoanApplicationError).errorType != null;
}

export const noanErrorResponses: Partial<
  Record<NoanError, { status: number; code: keyof typeof NoanError }>
> = {
  [NoanError.MissingPermission]: { status: 403, code: "MissingPermission" },
  [NoanError.ResourceNotAllowed]: { status: 403, code: "ResourceNotAllowed" },
  [NoanError.Unauthenticated]: { status: 401, code: "Unauthenticated" },
  [NoanError.ResourceNotFound]: { status: 404, code: "ResourceNotFound" },
  [NoanError.AccountInactive]: { status: 403, code: "AccountInactive" },
  [NoanError.PlanLimitReached]: { status: 402, code: "PlanLimitReached" },
};

/**
 * Shortcut to create an invariant error; does not throw
 */
export function invariant(message?: string) {
  return generateNoanError(NoanError.Invariant, message);
}

export function formatZodError(error: ZodError): ErrorResponse {
  const issues = error.issues.map((issue) => {
    const path = issue.path.join(".");
    return path ? `${path}: ${issue.message}` : issue.message;
  });

  return {
    code: "InvalidArguments",
    message: issues.join(", "),
  };
}
